import SEO from '../components/SEO';
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Shield, TrendingUp, Zap, Calculator, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const AscariiServiceHours = () => {
  const [selectedTermIndex, setSelectedTermIndex] = useState(1); // Default to 24 months
  const [desiredBucket, setDesiredBucket] = useState(50);
  const [desiredDays, setDesiredDays] = useState(6.5); // 50 hours / 8 = 6.25, round to 6.5 (nearest 0.5)

  const baseRate = 150; // GBP per hour PAYG rate
  const hoursPerDay = 8;

  const termOptions = [
    { label: '12 months', months: 12 },
    { label: '24 months', months: 24 },
    { label: '36 months', months: 36 }
  ];

  const selectedTerm = useMemo(() => termOptions[selectedTermIndex], [selectedTermIndex]);

  const plans = [
    { id: 'starter', name: 'Starter', tagline: 'Small bundle for ad-hoc needs', bucketHours: 25, volumeDiscount: 0 },
    { id: 'growth', name: 'Growth', tagline: 'Most popular for active teams', bucketHours: 50, volumeDiscount: 0.08 },
    { id: 'scale', name: 'Scale', tagline: 'Great for ongoing delivery', bucketHours: 100, volumeDiscount: 0.12 },
    { id: 'enterprise', name: 'Enterprise', tagline: 'Flexible capacity at scale', bucketHours: 200, volumeDiscount: 0.18 }
  ];

  const planPricing = useMemo(() => {
    return plans.map(p => {
      const effectiveRate = baseRate * (1 - p.volumeDiscount);
      const totalPrice = p.bucketHours * effectiveRate;
      const monthlyPayment = totalPrice / selectedTerm.months;
      const paygTotal = p.bucketHours * baseRate;
      const savingsTotal = paygTotal - totalPrice;
      const savingsPct = baseRate > 0 ? ((baseRate - effectiveRate) / baseRate) * 100 : 0;
      return { ...p, effectiveRate, totalPrice, monthlyPayment, savingsTotal, savingsPct };
    });
  }, [selectedTermIndex]);

  const discountForCredits = (credits) => {
    if (credits >= 200) return 0.18;
    if (credits >= 100) return 0.12;
    if (credits >= 50) return 0.08;
    return 0;
  };

  const customEffectiveRate = useMemo(() => baseRate * (1 - discountForCredits(desiredBucket)), [desiredBucket]);
  const customTotal = useMemo(() => desiredBucket * customEffectiveRate, [desiredBucket, customEffectiveRate]);
  const customMonthly = useMemo(() => customTotal / selectedTerm.months, [customTotal, selectedTerm]);
  const paygTotalForBucket = useMemo(() => desiredBucket * baseRate, [desiredBucket]);
  const customTotalSavings = useMemo(() => Math.max(0, paygTotalForBucket - customTotal), [paygTotalForBucket, customTotal]);
  const paygMonthlyForBucket = useMemo(() => (desiredBucket * baseRate) / selectedTerm.months, [desiredBucket, selectedTerm]);
  const estMonthlySavings = useMemo(() => Math.max(0, paygMonthlyForBucket - customMonthly), [paygMonthlyForBucket, customMonthly]);
  const estSavingsPct = useMemo(() => baseRate > 0 ? Math.max(0, ((baseRate - customEffectiveRate) / baseRate) * 100) : 0, [customEffectiveRate]);

  // Handlers for syncing hours and days
  const handleHoursChange = (hours) => {
    const newHours = Number(hours);
    setDesiredBucket(newHours);
    setDesiredDays(newHours / hoursPerDay);
  };

  const handleDaysChange = (days) => {
    const newDays = Number(days);
    setDesiredDays(newDays);
    setDesiredBucket(newDays * hoursPerDay);
  };

  const recommendedPlan = useMemo(() => {
    const sorted = [...plans].sort((a, b) => a.bucketHours - b.bucketHours);
    return sorted.find(p => p.bucketHours >= desiredBucket) || sorted[sorted.length - 1];
  }, [desiredBucket]);

  const formatCurrency = (amount) => `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const formatCurrency2 = (amount) => `£${amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Ascarii Service Hours | Flexible SAP Business One Support Bundles"
        description="Flexible professional services for SAP B1. Pre-purchased hours, no minimum contract, roll-over credits. Support, optimization, training on demand."
        keywords="SAP B1 support hours, flexible ERP support, SAP consulting, professional services, pay as you go support"
        canonical="https://www.ascarii.com/landing/ascarii-service-hours"
      />

      {/* Hero - Blue Gradient like other pages */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-sky-600 via-blue-700 to-cyan-600">
        <div className="absolute inset-0 z-0">
          <img src="https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?w=1920&auto=compress" alt="Professional Services" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">Professional Services</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">
                Ascarii <span className="text-cyan-300">Service Hours</span>
              </h1>
              <p className="text-xl md:text-2xl text-sky-100 mb-8 leading-relaxed">
                Buy Service Hours now and use them anytime over a 12, 24, or 36-month contract. One PO, predictable monthly payments, and better rates for larger bundles.
              </p>
              <ul className="space-y-3 text-white mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <span className="text-lg">Unlock work without PO delays</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <span className="text-lg">Spread the cost evenly over the term</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <span className="text-lg">Scale savings with more hours - up to 18% discount</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <span className="text-lg">Lock your rate for the entire term (price protection)</span>
                </li>
              </ul>
              <div className="flex flex-wrap gap-4">
                <a href="#pricing" className="inline-flex items-center justify-center rounded-xl bg-white text-sky-600 px-8 py-4 text-lg font-semibold hover:bg-sky-50 shadow-lg transition-colors">
                  See Bundles
                </a>
                <a href="#calculator" className="inline-flex items-center justify-center rounded-xl border-2 border-white text-white px-8 py-4 text-lg font-semibold hover:bg-white/10 shadow-lg transition-colors">
                  Price my bundle
                </a>
              </div>
              <p className="mt-6 text-sm text-sky-100">Standard PAYG rate: £150.00/hour. Prices exclude VAT.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "Up to 18%", label: "Volume Discount" },
              { value: "12-36", label: "Month Terms" },
              { value: "One PO", label: "Simple Procurement" },
              { value: "Quarterly", label: "Health Checks" }
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-sky-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 bg-white" id="pricing">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
            <h2 className="text-3xl font-extrabold text-gray-900">Service Hour Bundles & Pricing</h2>
            <div className="inline-flex rounded-2xl border-2 border-sky-300 bg-white p-1.5 shadow-sm">
              {termOptions.map((t, i) => (
                <button
                  key={t.months}
                  onClick={() => setSelectedTermIndex(i)}
                  className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition focus:outline-none ${
                    selectedTermIndex === i ? 'bg-sky-50 text-sky-800 ring-1 ring-sky-300 shadow-sm' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-4 gap-6">
            {planPricing.map((p) => (
              <div key={p.id} className={`relative pt-8 rounded-3xl border ${p.id === 'growth' ? 'border-sky-300' : 'border-gray-200'} bg-white p-6 shadow-sm`}>
                {p.id === 'growth' && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-3 py-1 rounded-full shadow-sm">
                    Most popular
                  </span>
                )}
                <div className="min-h-14">
                  <h3 className="text-xl font-bold text-gray-900">{p.name}</h3>
                  <p className="text-sm text-gray-600">{p.tagline}</p>
                </div>
                <div className="mt-6">
                  <div>
                    <span className="text-4xl font-extrabold text-gray-900">{formatCurrency(p.monthlyPayment)}</span>
                    <span className="text-sm font-normal text-gray-500">/mo</span>
                  </div>
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2 py-0.5 text-xs font-medium">
                      Price protected
                    </span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-700">
                      Bundle: <strong>{p.bucketHours} Service Hours</strong>
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      Effective rate: <strong>{formatCurrency2(p.effectiveRate)}/h</strong>
                    </p>
                    <p className="text-sm text-gray-700 mt-1">
                      Total value: <strong>{formatCurrency(p.totalPrice)}</strong>
                    </p>
                    <p className="text-emerald-700 mt-2 text-sm font-semibold">
                      Save ~{Math.round(p.savingsPct)}% vs PAYG
                    </p>
                    <p className="text-emerald-600 mt-1 text-lg font-bold">
                      Total savings: {formatCurrency(p.savingsTotal)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Consolidated Benefits Section */}
          <div className="mt-12 bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-8 border border-sky-200">
            <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">All Service Hour Bundles Include</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-sky-600 flex items-center justify-center mb-3">
                  <CheckCircle2 className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Use Anytime</h4>
                <p className="text-sm text-gray-600">Draw down hours as needed - yours to use whenever you need them</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-sky-600 flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Price Locked</h4>
                <p className="text-sm text-gray-600">Your rate is protected for the entire term</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-sky-600 flex items-center justify-center mb-3">
                  <Calculator className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Quarterly Health Check</h4>
                <p className="text-sm text-gray-600">Free reviews to optimise usage</p>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-sky-600 flex items-center justify-center mb-3">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">One PO</h4>
                <p className="text-sm text-gray-600">Single purchase order for the whole term</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Section - Combined with Guide */}
      <section className="py-16 bg-gray-50" id="calculator">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Service Hour Bundle Pricing Calculator</h2>
            <p className="text-xl text-gray-600">Calculate your savings and see monthly payment options</p>
          </div>

          {/* How to Use Guide */}
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-2xl p-8 mb-8 border border-blue-200">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="font-bold text-gray-900 mb-4 text-lg">Step 1: Choose Your Hours or Days</h3>
                <p className="text-gray-700 mb-4">
                  Select the Professional Service Hours you want using either slider. Use <strong>hours</strong> for precision or <strong>days</strong> for convenience (1 day = 8 hours).
                </p>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-blue-600">Yours to use:</span> These hours never expire - you've purchased them. Use whenever you need consulting, training, or support.
                  </p>
                </div>
              </div>
              
              <div>
                <h3 className="font-bold text-gray-900 mb-4 text-lg">Step 2: Select Your Contract Term</h3>
                <p className="text-gray-700 mb-4">
                  Choose to spread the cost over <strong>12, 24, or 36 months</strong>. Longer terms mean lower monthly payments.
                </p>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-blue-600">Complete flexibility:</span> Use all hours in month 1 or spread them throughout the term - entirely your choice.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Calculator Tool */}
          <div className="rounded-3xl border border-gray-200 bg-white p-6">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-6">
              <div>
                <h3 className="text-2xl font-extrabold text-gray-900">Configure Your Bundle</h3>
                <p className="text-gray-600 mt-1">Choose your hours and term to see monthly payments and savings vs PAYG.</p>
              </div>
              <div className="inline-flex rounded-2xl border-2 border-sky-300 bg-white p-1.5 shadow-sm">
                {termOptions.map((t, i) => (
                  <button
                    key={t.months}
                    onClick={() => setSelectedTermIndex(i)}
                    className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition focus:outline-none ${
                      selectedTermIndex === i ? 'bg-sky-50 text-sky-800 ring-1 ring-sky-300 shadow-sm' : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Hours</label>
                <div className="grid grid-cols-5 gap-3 items-center">
                  <div className="col-span-3">
                    <input
                      type="range"
                      min="5"
                      max="200"
                      step="5"
                      value={desiredBucket}
                      onChange={(e) => handleHoursChange(e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-600 focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-gray-600">Slider: {desiredBucket} hours</p>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      step="1"
                      value={desiredBucket}
                      onChange={(e) => handleHoursChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      className="w-full rounded-xl border border-gray-300 p-2 text-center focus:outline-none focus:ring-0"
                    />
                    <p className="mt-1 text-xs text-gray-500">Enter exact hours</p>
                  </div>
                </div>
              </div>

              {/* NEW: Days Slider */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-2">Days (8 hours per day)</label>
                <div className="grid grid-cols-5 gap-3 items-center">
                  <div className="col-span-3">
                    <input
                      type="range"
                      min="0.5"
                      max="100"
                      step="0.5"
                      value={desiredDays}
                      onChange={(e) => handleDaysChange(e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-sky-600 focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-gray-600">Slider: {desiredDays} days</p>
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      min="0.5"
                      max="100"
                      step="0.5"
                      value={desiredDays}
                      onChange={(e) => handleDaysChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      className="w-full rounded-xl border border-gray-300 p-2 text-center focus:outline-none focus:ring-0"
                    />
                    <p className="mt-1 text-xs text-gray-500">Enter exact days</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-blue-50 to-sky-50 p-6 mt-8">
              {/* Primary: Your Subscription Cost */}
              <div className="mb-6 p-6 bg-white rounded-xl border-2 border-blue-300 shadow-lg">
                <p className="text-sm font-semibold text-blue-600 mb-2">Your Subscription</p>
                <p className="text-4xl font-bold text-gray-900 mb-1">{formatCurrency(customMonthly)}/month</p>
                <p className="text-lg font-semibold text-emerald-600">Save {formatCurrency(estMonthlySavings)}/month (~{Math.round(estSavingsPct)}%)</p>
              </div>

              {/* Secondary: PAYG Comparison */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">PAYG equivalent</p>
                  <p className="text-xl font-bold text-gray-400">{formatCurrency(paygMonthlyForBucket)}/month</p>
                </div>
                <div className="p-4 bg-white rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">Total contract savings</p>
                  <p className="text-xl font-bold text-emerald-600">{formatCurrency(customTotalSavings)}</p>
                </div>
              </div>

              {/* Additional Info */}
              <div className="flex items-center justify-between text-sm">
                <p className="text-gray-600">Effective rate: <span className="font-semibold">{formatCurrency2(customEffectiveRate)}/h</span></p>
                <p className="text-sky-600">Nearest bundle: <strong>{recommendedPlan.name}</strong> ({recommendedPlan.bucketHours}h)</p>
              </div>
            </div>
            </div>
          </div>
      </section>

      {/* Benefits - Enhanced with Images and Sections */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Service Hour Bundles</h2>
            <p className="text-xl text-gray-600">Transform how you consume professional services</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Clock, title: 'Use hours when you need them', strapline: 'Flexible consumption', desc: "Your hours are always available - draw down as projects require. No pressure to use within a timeframe." },
              { icon: TrendingUp, title: 'Predictable payments', strapline: 'Budget with confidence', desc: 'Spread the payment over monthly instalments whilst your hours remain yours to use whenever needed.' },
              { icon: Zap, title: 'Scale savings', strapline: 'Volume discounts', desc: 'Larger bundles unlock lower effective hourly rates - up to 18% discount on Enterprise tier.' },
              { icon: Shield, title: 'Price protection', strapline: 'Lock your rate', desc: 'Your effective hourly rate is locked when you purchase - protection against future rate increases.' },
              { icon: Calculator, title: 'Quarterly health check', strapline: 'Optimise usage', desc: 'Free quarterly review to ensure you are using Ascarii Service Hours effectively.' },
              { icon: CheckCircle2, title: 'One PO simplicity', strapline: 'Streamline procurement', desc: 'Single purchase order for your Service Hours - no repeated approvals, cleaner admin.' }
            ].map((benefit, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true, amount: 0.2 }} className="group p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 hover:border-sky-300 hover:shadow-lg transition-all">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500/10 to-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-sky-500 group-hover:to-blue-500 transition-all">
                    <benefit.icon className="w-7 h-7 text-sky-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">{benefit.title}</h3>
                    <p className="text-sm text-sky-600 font-medium mb-2">{benefit.strapline}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{benefit.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - Value Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true, amount: 0.2 }}>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-4">How It Works</div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Transform Your Services Procurement</h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Traditional time and materials contracts create unpredictable costs and procurement delays. Service Hour Bundles give you control, predictability, and savings.
              </p>
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-sky-50 to-white rounded-xl border border-sky-200">
                  <h4 className="font-bold text-gray-900 mb-2">1. Choose Your Bundle</h4>
                  <p className="text-sm text-gray-700">Select hours based on your expected needs over 12, 24, or 36 months.</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-sky-50 to-white rounded-xl border border-sky-200">
                  <h4 className="font-bold text-gray-900 mb-2">2. Lock Your Rate</h4>
                  <p className="text-sm text-gray-700">Your effective hourly rate is locked for the entire term - price protection guaranteed.</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-sky-50 to-white rounded-xl border border-sky-200">
                  <h4 className="font-bold text-gray-900 mb-2">3. Use When Needed</h4>
                  <p className="text-sm text-gray-700">Draw down hours as projects require - no monthly quotas or use-it-or-lose-it pressure.</p>
                </div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true, amount: 0.2 }} className="relative group">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=85" alt="Team Planning" className="w-full h-[400px] object-cover transform group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-tl from-sky-600/20 to-transparent" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-gray-50" id="faqs">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              { q: 'What is a Service Hour Bundle?', a: 'A pre-purchased bundle of Professional Service Hours that are yours to use whenever you need them for consulting, configuration, training, and support. Your hours never expire whilst you remain an Ascarii customer.' },
              { q: 'Do unused hours roll over?', a: 'Your Service Hours are yours to keep - they never expire as long as you remain an Ascarii customer. There is no "use it or lose it". The hours you purchase are your investment in your SAP Business One solution, available whenever you need them.' },
              { q: 'Can we upgrade our bundle mid-term?', a: 'Yes - upgrades apply pro-rata and your monthly payment is adjusted accordingly to reflect the additional hours.' },
              { q: 'What counts as consulting time?', a: 'Design workshops, configuration, integration work, training, advisory sessions, and other project-related work under your agreement.' }
            ].map((faq, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{faq.q}</h3>
                <p className="text-gray-700">{faq.a}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-2xl border border-gray-200 bg-white p-5">
            <h3 className="font-semibold text-gray-900 mb-3">Terms & Definitions</h3>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
              <li><strong>One Service Hour equals one hour</strong> of professional services.</li>
              <li><strong>Minimum booking increment:</strong> 30-minute rounding per session.</li>
              <li>Service Hours are <strong>non-transferable</strong> between organisations.</li>
              <li>Scheduling coordinated by our PMO under standard booking process.</li>
              <li><strong>Price protection:</strong> Your effective hourly rate and monthly payment are locked for the selected term.</li>
            </ul>
          </div>
          <p className="mt-6 text-xs text-gray-500">All pricing excludes VAT. Terms subject to Master Services Agreement.</p>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-white" id="contact">
        <div className="container mx-auto px-4 lg:px-8 max-w-3xl text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">Ready to Make Services Predictable?</h2>
          <p className="mt-2 text-gray-600">Tell us what you're planning and we'll configure the right bundle and term for your roadmap.</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <a href="#" className="inline-flex items-center justify-center rounded-xl bg-sky-600 text-white py-3 font-semibold hover:bg-sky-700 transition-colors">
              Talk to Sales
            </a>
            <a href="#" className="inline-flex items-center justify-center rounded-xl border border-gray-300 py-3 font-semibold hover:bg-gray-100 transition-colors">
              Download One-Pager
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AscariiServiceHours;
